(() => {
  function start() {
    const dialog = document.querySelector('[data-cart-drawer]');
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.dataset.ready) return;
    dialog.dataset.ready = 'true';
    const content = dialog.querySelector('[data-drawer-content]');
    const notice = dialog.querySelector('[data-drawer-notice]');
    const message = dialog.querySelector('[data-drawer-message]');
    const retry = dialog.querySelector('[data-drawer-retry]');
    const cartUrl = dialog.dataset.cartUrl;
    let busy = false, stale = false, returnFocus = null;
    const pendingTempo = () => {try {return Boolean(localStorage.getItem('typo-tempo-cart-edit-v1'));} catch {return false;}};
    const showMessage = (text, refresh = false) => {
      notice.hidden = !text;
      message.textContent = text;
      retry.hidden = !refresh;
      notice.querySelector('a').hidden = !refresh;
    };
    const setBusy = value => {
      busy = value;
      dialog.setAttribute('aria-busy', String(value));
      content.inert = value || stale;
      retry.disabled = value;
    };
    const lock = () => {
      if (busy || stale) throw new Error('Please finish checking your bag before making another change.');
      setBusy(true);
      return () => setBusy(false);
    };
    const show = trigger => {
      if (dialog.open) return;
      returnFocus = trigger || document.activeElement;
      document.documentElement.classList.add('bag-drawer-open');
      dialog.showModal();
      document.querySelectorAll('[data-bag-link]').forEach(link => link.setAttribute('aria-expanded', 'true'));
    };
    const timedFetch = async (url, options = {}) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {return await fetch(url, {...options, signal: controller.signal});}
      finally {clearTimeout(timer);}
    };
    const json = async (url, body) => {
      const response = await timedFetch(url, {cache:'no-store', headers:{Accept:'application/json', ...(body ? {'Content-Type':'application/json'} : {})}, ...(body ? {method:'POST',body:JSON.stringify(body)} : {})});
      const data = await response.json().catch(() => null);
      if (!response.ok || !data) {
        const failure = new Error(typeof data?.description === 'string' ? data.description : 'We could not update your bag. Please check it before trying again.');
        failure.certain = response.status >= 400 && response.status < 500;
        throw failure;
      }
      return data;
    };
    const draft = () => {
      const root = content.querySelector('[data-bag-discovery]');
      return root ? {coffee:root.querySelector('[data-discovery-picker]')?.value,variant:root.querySelector('[data-discovery-variant]')?.value,grind:root.querySelector('[data-discovery-grind]')?.value} : null;
    };
    async function refresh(focus = null) {
      const selection = draft();
      const scrollTop = content.querySelector('[data-drawer-scroll]')?.scrollTop || 0;
      const url = new URL(cartUrl, location.origin);
      url.searchParams.set('section_id', 'commerce-cart-drawer');
      const response = await timedFetch(url.href, {cache:'no-store',headers:{Accept:'text/html'}});
      if (!response.ok) throw new Error('Your bag could not be refreshed.');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const state = parsed.querySelector('[data-drawer-state]');
      if (!state) throw new Error('Your bag could not be displayed.');
      content.replaceChildren(document.importNode(state, true));
      stale = false;
      window.TypoDiscovery?.start();
      const count = Number(state.dataset.count) || 0;
      dialog.querySelector('[data-drawer-count]').textContent = count;
      document.querySelectorAll('[data-bag-count]').forEach(node => {node.textContent = count;});
      document.querySelectorAll('[data-bag-link]').forEach(link => link.setAttribute('aria-label', `Bag, ${count} ${count === 1 ? 'item' : 'items'}`));
      const picker = content.querySelector('[data-discovery-picker]');
      if (selection && picker && [...picker.options].some(option => option.value === selection.coffee)) {
        picker.value = selection.coffee; picker.dispatchEvent(new Event('change'));
        const variant = content.querySelector('[data-discovery-variant]');
        const grind = content.querySelector('[data-discovery-grind]');
        if ([...variant.options].some(option => option.value === selection.variant)) variant.value = selection.variant;
        if ([...grind.options].some(option => option.value === selection.grind)) {
          grind.value = selection.grind;
          grind.dispatchEvent(new Event('change', { bubbles: true }));
        }
        variant.dispatchEvent(new Event('change'));
      }
      const scroll = content.querySelector('[data-drawer-scroll]');
      if (scroll) scroll.scrollTop = scrollTop;
      if (focus) {
        const line = [...content.querySelectorAll('[data-drawer-line]')].find(node => node.dataset.drawerLine === focus.key);
        // Wait until the refreshed controls are no longer inert before restoring focus.
        requestAnimationFrame(() => (line?.querySelector(focus.selector) || dialog.querySelector('[data-drawer-close]')).focus());
      }
    }
    async function updated(text = 'Your bag is up to date.', focus = null) {
      try {await refresh(focus);showMessage(text);}
      catch {
        stale = true;
        showMessage('Your change may already be saved. Refresh or view your full bag before trying again.', true);
      }
      setBusy(busy);
    }
    async function open(trigger) {
      if (pendingTempo()) {location.assign(cartUrl); return;}
      show(trigger);
      if (busy) return;
      setBusy(true); showMessage('Checking your bag...');
      await updated('');
      setBusy(false);
    }
    async function add(body, trigger, options = {}) {
      if (pendingTempo()) throw new Error('Open your bag to finish the last subscription edit before adding another coffee.');
      const release = lock();
      let saved = false, sent = false;
      try {
        // Validate cart-dependent offers while holding the same mutation lock as add.
        if (options.prepare) body = await options.prepare();
        sent = true;
        const response = await timedFetch(dialog.dataset.addUrl, {method:'POST',body,headers:{Accept:'application/json'}});
        const data = await response.json().catch(() => null);
        if (!response.ok || !data || data.status) {
          const failure = new Error(data?.description || data?.message || 'We could not confirm whether this coffee was added.');
          failure.certain = response.status >= 400 && response.status < 500;
          throw failure;
        }
        saved = true;
        options.beforeOpen?.();
        show(trigger);
        await updated('Added to your bag.');
      } catch (failure) {
        if (!sent) throw failure;
        if (failure.certain && !saved && !body.has('items[0][id]')) throw failure;
        options.beforeOpen?.();
        show(trigger);
        await updated(failure.certain ? `${failure.message} Check your bag before adding again; some coffees may already have been added.` : 'The connection was interrupted. Check the quantities in your bag before adding again.');
      } finally {release();}
      return {confirmed:saved};
    }
    async function change(line, quantity, focusSelector) {
      if (busy || stale) return;
      if (pendingTempo()) {location.assign(cartUrl); return;}
      const release = lock();
      const key = line.dataset.drawerLine;
      let sent = false;
      try {
        const current = await json(dialog.dataset.readUrl);
        const item = current.items.find(item => item.key === key);
        if (!item || item.quantity !== Number(line.dataset.originalQuantity)) {
          await updated('Your bag changed elsewhere. Please check the latest quantities.');
          return;
        }
        sent = true;
        await json(dialog.dataset.changeUrl, {id:key,quantity});
        await updated(quantity === 0 ? 'Item removed from your bag.' : 'Quantity updated.', {key,selector:focusSelector});
      } catch (failure) {
        const text = sent && !failure.certain ? 'The connection was interrupted. Check your bag before changing the quantity again.' : failure.message;
        await updated(text, {key,selector:focusSelector});
      } finally {release();}
    }
    dialog.addEventListener('click', event => {
      if (event.target === dialog) {
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
      }
      if (event.target.closest('[data-drawer-close]')) dialog.close();
      const control = event.target.closest('[data-line-delta],[data-line-remove]');
      const line = control?.closest('[data-drawer-line]');
      if (line) change(line, control.hasAttribute('data-line-remove') ? 0 : Math.max(0, Number(line.dataset.originalQuantity) + Number(control.dataset.lineDelta)), control.hasAttribute('data-line-remove') ? '[data-line-quantity]' : `[data-line-delta="${control.dataset.lineDelta}"]`);
    });
    const commitQuantity = input => {
      const line = input.closest('[data-drawer-line]');
      if (!input.checkValidity() || input.value === '') {input.reportValidity();input.value = line.dataset.originalQuantity;return;}
      if (Number(input.value) !== Number(line.dataset.originalQuantity)) change(line, Number(input.value), '[data-line-quantity]');
    };
    dialog.addEventListener('change', event => {if(event.target.matches('[data-line-quantity]')) commitQuantity(event.target);});
    dialog.addEventListener('keydown', event => {
      if (event.key === 'Tab') {
        const focusable = [...dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter(node => node.getClientRects().length && !node.closest('[inert]'));
        const first = focusable[0], last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) {event.preventDefault();last?.focus();}
        else if (!event.shiftKey && document.activeElement === last) {event.preventDefault();first?.focus();}
      }
      if (event.key === 'Enter' && event.target.matches('[data-line-quantity]')) {event.preventDefault();commitQuantity(event.target);}
    });
    dialog.addEventListener('submit', event => {
      if (event.target.getAttribute('id') === 'DrawerCart' && (busy || stale || pendingTempo())) {
        event.preventDefault();
        if (pendingTempo()) location.assign(cartUrl);
      }
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('bag-drawer-open');
      document.querySelectorAll('[data-bag-link]').forEach(link => link.setAttribute('aria-expanded','false'));
      if (returnFocus?.isConnected) returnFocus.focus({preventScroll:true});
    });
    retry.addEventListener('click', () => open());
    document.querySelectorAll('[data-bag-link]').forEach(link => {
      if (document.body.dataset.page === 'cart') return;
      link.setAttribute('aria-haspopup','dialog');link.setAttribute('aria-controls',dialog.id);link.setAttribute('aria-expanded','false');
      link.addEventListener('click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();open(link);
      });
    });
    window.CommerceBag = {open, add, lock, updated, busy:() => busy || stale};
    window.addEventListener('pageshow', event => {if(event.persisted && dialog.open)open();});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start); else start();
})();
